import React, { useEffect, useMemo, useState } from 'react'
import api from '../../utils/api'

/**
 * EditInvoiceModal — popup form for editing an existing (un-paid) invoice.
 * Three steps: pick which invoice → edit fields → preview & update.
 */

function fmtMoney(n) {
    const v = Number(n || 0)
    return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function isPaid(p) {
    const s = (p.rawStatus || p.status || '').toLowerCase()
    return s === 'paid' || s === 'paid_manually'
}

export default function EditInvoiceModal({ isOpen, onClose, caseData, onSuccess, onToast }) {
    const [step, setStep] = useState(1)
    const [selected, setSelected] = useState(null)
    const [customerName, setCustomerName] = useState('')
    const [emailCsv, setEmailCsv] = useState('')
    const [address, setAddress] = useState('')
    const [dueDate, setDueDate] = useState('')
    const [items, setItems] = useState([])
    const [includeProcessingFee, setIncludeProcessingFee] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        setStep(1)
        setSelected(null)
        setCustomerName('')
        setEmailCsv('')
        setAddress('')
        setDueDate('')
        setItems([])
        setIncludeProcessingFee(false)
    }, [isOpen])

    const editable = useMemo(() => (caseData?.payments || []).filter((p) => !isPaid(p)), [caseData])

    const partyName = (clientId) => {
        if (clientId === caseData?.plaintiff?.clientId || clientId === caseData?.plaintiff?.client_id) return `Plaintiff: ${caseData.plaintiff.name || ''}`
        if (clientId === caseData?.defendant?.clientId || clientId === caseData?.defendant?.client_id) return `Defendant: ${caseData.defendant.name || ''}`
        return '—'
    }

    const pickInvoice = (p) => {
        setSelected(p)
        setCustomerName(p.invoiceCustomerName || partyName(p.clientId))
        const emails = Array.isArray(p.invoiceEmails)
            ? p.invoiceEmails.join(', ')
            : (typeof p.invoiceEmails === 'string' ? p.invoiceEmails : '')
        setEmailCsv(emails)
        setAddress(p.invoiceAddress || '')
        setDueDate(p.dueDate ? String(p.dueDate).slice(0, 10) : '')
        setItems(Array.isArray(p.items) && p.items.length ? p.items : [{ description: '', hours: 0, rate: 0 }])
        setIncludeProcessingFee(!!p.includeProcessingFee)
        setStep(2)
    }

    const itemTotal = useMemo(() => items.reduce((s, it) => s + Number(it.hours || 0) * Number(it.rate || 0), 0), [items])
    const processingFee = includeProcessingFee ? Math.round(itemTotal * 0.022 * 100) / 100 + 0.30 : 0
    const grandTotal = itemTotal + processingFee

    const updateItem = (i, patch) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, ...patch } : it))
    const addItem = () => setItems((p) => [...p, { description: '', hours: 0, rate: 0 }])
    const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i))

    const handleSave = async () => {
        if (!selected) return
        try {
            setSubmitting(true)
            const lineItems = items
                .filter((it) => it.description || it.hours || it.rate)
                .map((it) => ({
                    description: it.description,
                    quantity: Number(it.hours || 1),
                    unit_price: Number(it.rate || 0),
                    amount: Number(it.hours || 0) * Number(it.rate || 0),
                }))
            // Send field names that match the DB columns on `payments`.
            // `emails` was wrong — the column is `invoice_emails`.
            await api.put(`/billing/${selected.id}`, {
                invoice_customer_name: customerName,
                invoice_emails: emailCsv.split(',').map((s) => s.trim()).filter(Boolean).join(', '),
                invoice_address: address || null,
                due_date: dueDate,
                items: lineItems,
                include_processing_fee: includeProcessingFee,
                amount: itemTotal,
                grand_total: grandTotal,
            })
            onToast?.({ type: 'success', message: 'Invoice updated' })
            onSuccess?.()
            onClose()
        } catch (err) {
            onToast?.({ type: 'error', message: err?.response?.data?.message || 'Failed to update invoice' })
        } finally {
            setSubmitting(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => !submitting && onClose()}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Edit Invoice</h3>
                        <p className="text-xs text-gray-500">{caseData?.caseName} · #{caseData?.caseNumber}</p>
                    </div>
                    <div className="flex gap-1">
                        {[1, 2, 3].map((n) => <div key={n} className={`w-8 h-1.5 rounded-full ${step >= n ? 'bg-teal-500' : 'bg-gray-200'}`} />)}
                    </div>
                </div>
                <div className="overflow-y-auto flex-1">
                    {step === 1 && (
                        <div className="p-6 space-y-3">
                            <h3 className="text-sm font-semibold text-gray-700">Pick which invoice to edit</h3>
                            {editable.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-8">No editable (unpaid) invoices on this case.</p>
                            ) : editable.map((p) => (
                                <button key={p.id} type="button" onClick={() => pickInvoice(p)}
                                    className="w-full text-left p-4 border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 rounded-lg transition-colors">
                                    <div className="flex justify-between">
                                        <div>
                                            <div className="font-medium text-gray-900">{p.invoiceNumber || `Invoice #${p.id}`}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">{partyName(p.clientId)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-semibold text-gray-900">{fmtMoney(p.grandTotal || p.amount)}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">{p.dueDate ? `Due ${new Date(p.dueDate).toLocaleDateString()}` : ''}</div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                    {step === 2 && selected && (
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Customer Name</label>
                                    <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                                    <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Emails (comma-separated)</label>
                                    <input value={emailCsv} onChange={(e) => setEmailCsv(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Billing Address</label>
                                    <input value={address} onChange={(e) => setAddress(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Street, City, State, ZIP" />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-medium text-gray-700">Line Items</label>
                                    <button type="button" onClick={addItem} className="text-xs text-teal-600 hover:text-teal-700 font-medium">+ Add Item</button>
                                </div>
                                <div className="space-y-2">
                                    {items.map((it, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                            <input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="Description" className="col-span-6 px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                                            <input type="number" value={it.hours} onChange={(e) => updateItem(i, { hours: e.target.value })} placeholder="Hours" className="col-span-2 px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                                            <input type="number" value={it.rate} onChange={(e) => updateItem(i, { rate: e.target.value })} placeholder="Rate" className="col-span-2 px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                                            <div className="col-span-1 text-sm text-gray-700 font-medium">{fmtMoney((it.hours || 0) * (it.rate || 0))}</div>
                                            <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-red-500 hover:text-red-700 text-xs">×</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={includeProcessingFee} onChange={(e) => setIncludeProcessingFee(e.target.checked)} className="w-4 h-4" />
                                <span>Include Stripe processing fee (2.2% + $0.30)</span>
                            </label>
                            <div className="flex justify-between items-center pt-4 border-t">
                                <button type="button" onClick={() => setStep(1)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm">← Back</button>
                                <button type="button" onClick={() => setStep(3)} className="px-4 py-2 bg-[#173336] text-white rounded-md text-sm font-medium">Preview →</button>
                            </div>
                        </div>
                    )}
                    {step === 3 && selected && (
                        <div className="p-6 space-y-4">
                            <div className="bg-gray-50 rounded-lg p-5">
                                <h3 className="text-sm font-semibold mb-2">{customerName}</h3>
                                <p className="text-xs text-gray-500 mb-3">Invoice {selected.invoiceNumber} · Due {dueDate}</p>
                                <table className="w-full text-sm">
                                    <thead className="text-left text-xs text-gray-500 uppercase border-b">
                                        <tr><th className="py-1">Description</th><th>Hours</th><th>Rate</th><th className="text-right">Amount</th></tr>
                                    </thead>
                                    <tbody>
                                        {items.filter((it) => it.description || it.hours || it.rate).map((it, i) => (
                                            <tr key={i} className="border-b last:border-b-0">
                                                <td className="py-2">{it.description || '—'}</td>
                                                <td>{it.hours}</td>
                                                <td>{fmtMoney(it.rate)}</td>
                                                <td className="text-right">{fmtMoney((it.hours || 0) * (it.rate || 0))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="text-right space-y-1 pt-2 border-t mt-2">
                                    <div className="text-sm">Subtotal: {fmtMoney(itemTotal)}</div>
                                    {includeProcessingFee && <div className="text-sm">Processing Fee: {fmtMoney(processingFee)}</div>}
                                    <div className="text-base font-semibold">Total: {fmtMoney(grandTotal)}</div>
                                </div>
                            </div>
                            <div className="flex justify-between items-center pt-4 border-t">
                                <button type="button" onClick={() => setStep(2)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm">← Back</button>
                                <button type="button" disabled={submitting} onClick={handleSave} className="px-4 py-2 bg-[#173336] text-white rounded-md text-sm font-medium disabled:opacity-50">
                                    {submitting ? 'Updating…' : 'Update Invoice'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
